import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react'
import { ChevronIcon, DownloadIcon } from '../../../shared/ui/icons'
import { SearchIcon } from '../../../shared/ui/dashIcons'

/**
 * The school tier's filter controls — one look for every toolbar that narrows a
 * list: Children, Classes, Teachers and the play log.
 *
 * 🔴 ONE HEIGHT FOR EVERYTHING IN A ROW. Every control here is `h-10`, so a
 * search box, a select, a date and a button sit on one baseline instead of the
 * staircase that four hand-rolled paddings drew.
 *
 * 🔴 A FILTER THAT IS DOING SOMETHING LOOKS LIKE IT. A select left on "All"
 * reads as resting; one that is narrowing the list is tinted, and the chips row
 * under the toolbar names every filter in force with a way to drop each one. A
 * reader who sees "3 children" should never have to hunt for why.
 */

const BASE =
  'h-10 rounded-xl border font-dash text-dash-xs font-bold outline-none transition-colors duration-150 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20'

const RESTING = 'border-line bg-canvas text-ink hover:border-line-strong'
const ENGAGED = 'border-primary bg-primary/5 text-primary'

/** Search, with a clear button once there is something to clear. */
export function FilterSearch({
  value,
  onChange,
  placeholder,
  label,
  className = '',
}: {
  value: string
  onChange: (next: string) => void
  placeholder: string
  label: string
  className?: string
}) {
  return (
    <div className={`relative w-full sm:w-64 ${className}`}>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted"
      >
        <SearchIcon size={16} />
      </span>
      <input
        type="search"
        value={value}
        onChange={(e) => { onChange(e.target.value) }}
        placeholder={placeholder}
        aria-label={label}
        className={`${BASE} ${value === '' ? RESTING : ENGAGED} w-full pr-9 pl-9 font-medium placeholder:font-medium placeholder:text-muted [&::-webkit-search-cancel-button]:hidden`}
      />
      {value !== '' && (
        <button
          type="button"
          onClick={() => { onChange('') }}
          aria-label="Clear search"
          className="absolute top-1/2 right-2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface hover:text-ink"
        >
          <ClearGlyph />
        </button>
      )}
    </div>
  )
}

/**
 * A select with the kit's chevron.
 *
 * ⚠️ `appearance-none` PLUS THE CHEVRON, `AdminSelect`'s rule: a bare `<select>`
 * draws the platform's grey chrome in the middle of a cream page.
 */
export function FilterSelect({
  active,
  children,
  className = '',
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & {
  /** True when the value is narrowing the list, i.e. not the "All …" option. */
  active: boolean
}) {
  return (
    <span className={`relative block w-full sm:w-auto ${className}`}>
      <select
        {...rest}
        className={`${BASE} ${active ? ENGAGED : RESTING} w-full cursor-pointer appearance-none pr-9 pl-3 sm:min-w-[10.5rem]`}
      >
        {children}
      </select>
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 rotate-90 ${active ? 'text-primary' : 'text-muted'}`}
      >
        <ChevronIcon size={14} />
      </span>
    </span>
  )
}

export function FilterDate(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      type="date"
      {...props}
      className={`${BASE} ${RESTING} w-full cursor-pointer px-3 sm:w-auto`}
    />
  )
}

/** A small label over a control, for toolbars that carry several of them. */
export function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="font-dash text-dash-2xs font-bold tracking-wide text-muted uppercase">
        {label}
      </span>
      {children}
    </label>
  )
}

/** Quick-pick windows ("Last 7 days"), drawn as one segmented group. */
export function FilterPresets<T extends string | number>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly { value: T; label: string }[]
  value: T | null
  onChange: (next: T) => void
  label: string
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex h-10 items-center gap-1 rounded-xl border border-line bg-canvas p-1"
    >
      {options.map((o) => {
        const on = o.value === value
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => { onChange(o.value) }}
            className={`h-full cursor-pointer rounded-lg px-3 font-dash text-dash-xs font-bold whitespace-nowrap transition-colors ${
              on ? 'bg-primary text-white shadow-card' : 'text-ink-soft hover:bg-surface hover:text-ink'
            }`}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

/** The toolbar's secondary action — Export, mostly. */
export function FilterAction({
  onClick,
  disabled,
  children,
  icon = <DownloadIcon size={16} />,
}: {
  onClick: () => void
  disabled?: boolean
  children: ReactNode
  icon?: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${BASE} ${RESTING} flex cursor-pointer items-center justify-center gap-2 px-3.5 hover:bg-surface disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {icon}
      <span>{children}</span>
    </button>
  )
}

export interface FilterChip {
  key: string
  label: string
  onRemove: () => void
}

/**
 * What is narrowing the list, in words, with a way out of each.
 *
 * ⚠️ IT DRAWS NOTHING WHEN NOTHING IS APPLIED. An empty row that says "No
 * filters" is a line of chrome saying the default out loud.
 */
export function ActiveFilters({
  chips,
  onClearAll,
  summary,
}: {
  chips: readonly FilterChip[]
  onClearAll: () => void
  /** "Showing 4 of 32 children" — the consequence of the chips. */
  summary?: ReactNode
}) {
  if (chips.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-2">
      {summary !== undefined && (
        <span className="mr-1 font-dash text-dash-xs font-bold text-muted">{summary}</span>
      )}
      {chips.map((c) => (
        <span
          key={c.key}
          className="inline-flex h-7 items-center gap-1 rounded-pill border border-primary/30 bg-primary/5 pr-1 pl-3 font-dash text-dash-xs font-bold text-primary"
        >
          {c.label}
          <button
            type="button"
            onClick={c.onRemove}
            aria-label={`Remove filter: ${c.label}`}
            className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-pill transition-colors hover:bg-primary/15"
          >
            <ClearGlyph size={12} />
          </button>
        </span>
      ))}
      {chips.length > 1 && (
        <button
          type="button"
          onClick={onClearAll}
          className="ml-1 cursor-pointer font-dash text-dash-xs font-bold text-muted underline-offset-2 transition-colors hover:text-ink hover:underline"
        >
          Clear all
        </button>
      )}
    </div>
  )
}

/*
 * ⚠️ A DISMISS GLYPH DRAWN HERE, NOT `dashIcons`' `CrossIcon` — that one is the
 * mark for an absent child and says so in its own note.
 */
function ClearGlyph({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 7l10 10M17 7 7 17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}

/**
 * Hand a CSV string to the browser as a download.
 *
 * ⚠️ THE CALLER ESCAPES. This only moves bytes; each model's own `…ToCsv` owns
 * quoting and the leading-`=` guard.
 */
export function downloadCsv(csv: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * The table row for "the filters left nothing", with the way back beside it.
 *
 * ⚠️ THE BUTTON ONLY WHEN A FILTER IS APPLIED. An empty list with nothing
 * narrowing it is a different state, and `DataSurface` already owns that one.
 */
export function NoMatches({ noun, onClear }: { noun: string; onClear?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-10 text-center">
      <span className="flex h-11 w-11 items-center justify-center rounded-pill bg-canvas text-muted">
        <SearchIcon size={20} />
      </span>
      <p className="font-dash text-dash-sm font-bold text-ink">No {noun} match these filters</p>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className={`${BASE} ${RESTING} cursor-pointer px-4 hover:bg-surface`}
        >
          Clear filters
        </button>
      )}
    </div>
  )
}
